/**
 * Responsibility Store
 * 
 * Manages canonical responsibility queues for the current user.
 * 
 * Per Doc 06 (Queues & Query Truth Table):
 * - Responsibility classification is SERVER-SIDE canonical truth
 * - UI-side filtering or inference is FORBIDDEN for canonical queues
 * - Each item appears in exactly one responsibility queue
 * 
 * Per Doc 08 (Home Dashboard and Next-Step Engine):
 * - Classifications: REQUIRED | WAITING | NONE
 * - Server decides classification; UI renders only
 * 
 * This store provides:
 * - fetchResponsibilities(userId) - calls get_responsibilities RPC
 * - refreshResponsibilities() - re-fetches after click errors
 * - responsibilities - the server-authoritative list
 * - grouped - PRE-COMPUTED grouping for display (NOT reclassification)
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import type {
  ResponsibilityCard,
  ResponsibilitiesResponse,
  ResponsibilitySummary,
  ResponsibilityScope,
  GroupedResponsibilities,
} from '../../types/prioritization/responsibility';

// ============================================================================
// Store State Interface
// ============================================================================

interface ResponsibilityState {
  /** 
   * Server-authoritative list of responsibilities.
   * Classification is final and must not be modified by UI.
   */
  responsibilities: ResponsibilityCard[];
  
  /**
   * PRE-COMPUTED grouped responsibilities.
   * Computed once when data is fetched, not on every render.
   * This prevents infinite loops from selector-created arrays.
   */
  requiredItems: ResponsibilityCard[];
  waitingItems: ResponsibilityCard[];
  noneItems: ResponsibilityCard[];
  
  /** Summary counts from the server */
  summary: ResponsibilitySummary;
  
  /** Timestamp of last fetch */
  fetchedAt: string | null;
  
  /** Loading state */
  isLoading: boolean;
  
  /** Error state */
  error: string | null;
  
  /** 
   * Stale data warning flag.
   * Set when a deep-link click fails and we need to refresh.
   */
  isStale: boolean;
  
  // ========================================================================
  // Actions
  // ========================================================================
  
  /**
   * Fetch responsibilities from the server.
   * This is the ONLY way to get responsibility data.
   * Classification is server-authoritative.
   */
  fetchResponsibilities: (userId: string) => Promise<void>;
  
  /**
   * Refresh responsibilities after a click error.
   * Marks data as stale temporarily, then refetches.
   */
  refreshResponsibilities: (userId: string) => Promise<void>;
  
  /**
   * Mark data as stale (e.g., after a failed navigation).
   * UI should show warning until refresh completes.
   */
  markAsStale: () => void;
  
  /**
   * Clear store state.
   */
  reset: () => void;

  /**
   * Return pre-computed groups for display (required / waiting / none).
   * Per Doc 06: display grouping only, NOT reclassification.
   */
  getGrouped: () => GroupedResponsibilities;

  /**
   * Return responsibilities filtered by scope (TASK, SESSION, CATCHBALL, CYCLE).
   */
  getByScope: (scope: ResponsibilityScope) => ResponsibilityCard[];
}

// ============================================================================
// Initial State
// ============================================================================

const INITIAL_SUMMARY: ResponsibilitySummary = {
  required_count: 0,
  waiting_count: 0,
  none_count: 0,
  total_count: 0,
};

// ============================================================================
// Helper: Group responsibilities once
// ============================================================================

function computeGroups(responsibilities: ResponsibilityCard[]) {
  return {
    requiredItems: responsibilities.filter(r => r.classification === 'REQUIRED'),
    waitingItems: responsibilities.filter(r => r.classification === 'WAITING'),
    noneItems: responsibilities.filter(r => r.classification === 'NONE'),
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useResponsibilityStore = create<ResponsibilityState>()((set, get) => ({
  responsibilities: [],
  requiredItems: [],
  waitingItems: [],
  noneItems: [],
  summary: INITIAL_SUMMARY,
  fetchedAt: null,
  isLoading: false,
  error: null,
  isStale: false,
  
  // ========================================================================
  // Fetch Responsibilities (Server-Authoritative)
  // ========================================================================
  
  fetchResponsibilities: async (userId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      console.warn('[ResponsibilityStore] Supabase not available');
      return;
    }
    
    if (!userId) {
      console.warn('[ResponsibilityStore] No userId provided');
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      // Call the canonical RPC function
      const { data, error } = await supabase.rpc('get_responsibilities', {
        p_user_id: userId,
      });
      
      if (error) {
        console.error('[ResponsibilityStore] RPC error:', error);
        set({
          isLoading: false,
          error: error.message,
        });
        return;
      }
      
      // Parse the response
      const response = data as ResponsibilitiesResponse;
      const responsibilities = response.responsibilities || [];
      
      // Pre-compute groups ONCE here, not in selectors
      const groups = computeGroups(responsibilities);
      
      set({
        responsibilities,
        ...groups,
        summary: response.summary || INITIAL_SUMMARY,
        fetchedAt: response.fetched_at || new Date().toISOString(),
        isLoading: false,
        isStale: false,
        error: null,
      });
      
      console.log(
        `[ResponsibilityStore] Loaded ${response.summary?.total_count || 0} responsibilities ` +
        `(${response.summary?.required_count || 0} required, ` +
        `${response.summary?.waiting_count || 0} waiting, ` +
        `${response.summary?.none_count || 0} none)`
      );
    } catch (err) {
      console.error('[ResponsibilityStore] Exception:', err);
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },
  
  // ========================================================================
  // Refresh After Click Error
  // ========================================================================
  
  refreshResponsibilities: async (userId: string) => {
    console.log('[ResponsibilityStore] Refreshing responsibilities after click error...');
    
    // Mark as stale first so UI can show warning
    set({ isStale: true });
    
    // Re-fetch from server
    await get().fetchResponsibilities(userId);
  },
  
  // ========================================================================
  // Mark As Stale
  // ========================================================================
  
  markAsStale: () => {
    set({ isStale: true });
  },
  
  // ========================================================================
  // Reset
  // ========================================================================
  
  reset: () => {
    set({
      responsibilities: [],
      requiredItems: [],
      waitingItems: [],
      noneItems: [],
      summary: INITIAL_SUMMARY,
      fetchedAt: null,
      isLoading: false,
      error: null,
      isStale: false,
    });
  },

  // ========================================================================
  // Accessors (for tests and surfaces that expect getGrouped / getByScope)
  // ========================================================================

  getGrouped: (): GroupedResponsibilities => {
    const state = get();
    return {
      required: state.requiredItems,
      waiting: state.waitingItems,
      none: state.noneItems,
    };
  },

  getByScope: (scope: ResponsibilityScope): ResponsibilityCard[] => {
    return get().responsibilities.filter(r => r.scope === scope);
  },
}));

// ============================================================================
// Selector Hooks
// 
// These selectors return STABLE references from the store.
// They do NOT compute/filter inside the selector to avoid infinite loops.
// ============================================================================

/**
 * Get just the REQUIRED responsibilities.
 * Returns the pre-computed array from the store (stable reference).
 */
export const useRequiredResponsibilities = () =>
  useResponsibilityStore(state => state.requiredItems);

/**
 * Get just the WAITING responsibilities.
 * Returns the pre-computed array from the store (stable reference).
 */
export const useWaitingResponsibilities = () =>
  useResponsibilityStore(state => state.waitingItems);

/**
 * Get just the NONE responsibilities.
 * Returns the pre-computed array from the store (stable reference).
 */
export const useNoneResponsibilities = () =>
  useResponsibilityStore(state => state.noneItems);

/**
 * Get the summary counts.
 * Returns the summary object from the store (stable reference).
 */
export const useResponsibilitySummary = () =>
  useResponsibilityStore(state => state.summary);

/**
 * Get loading state.
 */
export const useResponsibilityLoading = () =>
  useResponsibilityStore(state => state.isLoading);

/**
 * Get error state.
 */
export const useResponsibilityError = () =>
  useResponsibilityStore(state => state.error);

/**
 * Get stale state.
 */
export const useResponsibilityIsStale = () =>
  useResponsibilityStore(state => state.isStale);

/**
 * Get fetchedAt timestamp.
 */
export const useResponsibilityFetchedAt = () =>
  useResponsibilityStore(state => state.fetchedAt);

/**
 * Get loading and error state as individual primitives.
 * Components should destructure what they need.
 */
export const useResponsibilityStatus = () => {
  const isLoading = useResponsibilityStore(state => state.isLoading);
  const error = useResponsibilityStore(state => state.error);
  const isStale = useResponsibilityStore(state => state.isStale);
  const fetchedAt = useResponsibilityStore(state => state.fetchedAt);
  return { isLoading, error, isStale, fetchedAt };
};

/**
 * Check if responsibilities need refresh.
 * Returns true if stale or never fetched.
 */
export const useNeedsRefresh = () =>
  useResponsibilityStore(state => state.isStale || state.fetchedAt === null);
